import { ReactNode } from 'react';
import { ColumnsType } from 'antd/es/table';
import { TFunction } from 'i18next';
import { BoxForListing as Box, LockStatusMarker, useWatchLockState } from 'entities/Box';
import { ReactComponent as ContractsTableIcon } from 'shared/assets/icons/ContractsTableIcon.svg';
import { ReactComponent as LockIcon } from 'shared/assets/icons/LockIcon.svg';
import { Paragraph } from 'shared/ui/Paragraph';
import { Toggle } from 'shared/ui/Toggle';
import { getClientDateFormat, getClientDateWithTimeFormat } from 'shared/utils/helpers/getDateFormat';
import { useRole } from 'entities/User';
import { SizeCodeType } from 'entities/SizeCode';
import { Warehouse } from 'entities/Warehouse';
import { getVatRateAmount } from 'entities/Contract';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { formatPrice } from 'shared/utils/helpers/formatPrice';
import { VatRate } from 'entities/VatRate';
import { ActivationBoxModal } from 'features/ActivationBoxModal';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';
import { BoxLockStatus } from './BoxLockStatus';

interface Args {
  isDesktop: boolean;
  t: TFunction;
  openBoxInfoModal: (box: Box) => void;
  currencySymbol: string;
  goToUserContracts: (userId: string | undefined) => void;
  openContractDetails: (contractId: string | undefined) => void;
  openReservationDetails: (reservationId: string | undefined) => void;
  warehouses: Warehouse[];
  showPriceWithVat: boolean;
  vatRate?: Nullable<VatRate>;
}

export const renderBoxListColumns = ({
  isDesktop,
  openBoxInfoModal,
  currencySymbol,
  openReservationDetails,
  openContractDetails,
  goToUserContracts,
  t,
  warehouses,
  showPriceWithVat,
  vatRate,
}: Args): ColumnsType<Box> => {
  const { isManagement } = useRole();

  const mobileColumns: ColumnsType<Box> = [
    {
      title: t('Box'),
      key: 'name',
      render: (_, record) => (
        <div>
          <div
            className="text-accent font-semibold underline cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              openBoxInfoModal(record);
            }}
          >
            {record.name}
          </div>
          {record.lockAddress && (
            <div className="flex items-center py-2">
              <LockIcon className="w-4 h-4 mr-1 stroke-accent" />
              <span className="text-primary text-xs">{record.lockAddress}</span>
            </div>
          )}
          <div className="w-fit">
            <BoxLockStatus box={record} warehouseId={record.warehouseId} />
          </div>
        </div>
      ),
    },
    {
      title: t('Square'),

      key: 'square',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (_, record) => (
        <div className="flex flex-col">
          <div className="text-sm">{record.warehouseName}</div>
          <div className="font-bold">
            {formatDecimal(record.square)} м<sup className="font-bold">{record.sizeCodeType === SizeCodeType.CUBIC_BOX ? 3 : 2}</sup>
          </div>
        </div>
      ),
    },
  ];

  const desktopColumns: ColumnsType<Box> = [
    {
      title: t('Cost'),
      key: 'monthRate',
      render: (_, record) => {
        const renderPriceWithVat = (): ReactNode => {
          const vatRateAmount = vatRate ? getVatRateAmount(record.monthRate || 0, vatRate.ratePercent) : 0;
          const rateWithVat = roundNumber((record.monthRate || 0) + vatRateAmount);

          return (
            <div className="mb-2">
              {t('Brutto')}: <br />
              {formatPrice(rateWithVat)} {currencySymbol} <span className="text-xs text-primaryLight">/{t('Month')}</span>
            </div>
          );
        };

        return (
          <div className="text-primary">
            {record.dailyRate && (
              <div>
                {formatPrice(record.dailyRate)} {currencySymbol}
                <span className="text-sm text-primaryLight">/{t('Day')}</span>
              </div>
            )}
            {record.weekRate && (
              <div>
                {formatPrice(record.weekRate)} {currencySymbol}
                <span className="text-sm text-primaryLight">/{t('Week')}</span>
              </div>
            )}
            {record.monthRate && (
              <div className="text-center">
                {showPriceWithVat && renderPriceWithVat()}
                <div className={`text-${showPriceWithVat ? 'xs' : 'sm'}`}>
                  {showPriceWithVat ? (
                    <>
                      {t('Netto')}: <br />
                    </>
                  ) : (
                    ''
                  )}
                  {formatPrice(record.monthRate)} {currencySymbol}/{t('Month')}
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('Customer'),
      key: 'user',
      render: (_, record) => (
        <div>
          <div className="flex gap-2 text-primary font-normal">
            {record?.userId && (
              <div>
                <div className="flex gap-2 text-primary font-normal">
                  {record.userEmail}
                  <ContractsTableIcon
                    className="cursor-pointer"
                    onClick={() => {
                      goToUserContracts(record.userId);
                    }}
                  />
                </div>
                {record?.contractId && (
                  <div className="text-primary font-normal">
                    {t('Contract')}{' '}
                    <span
                      className="text-accent underline cursor-pointer"
                      onClick={() => {
                        openContractDetails(record.contractId);
                      }}
                    >
                      {record.contractNumber}
                    </span>
                  </div>
                )}
                {record.reservationId && (
                  <div className="text-primary font-normal">
                    {t('Reservation')}{' '}
                    <span
                      className="text-accent underline cursor-pointer"
                      onClick={() => {
                        openReservationDetails(record.reservationId);
                      }}
                    >
                      {record.reservationNumber}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('Last opened'),
      key: 'lastOpened',
      render: (_, record) => <div className="text-primary font-normal">{getClientDateWithTimeFormat(record.lastOpened)}</div>,
    },
    {
      title: t('Renting info'),
      key: 'rentingInfo',

      render: (_, record) => (
        <>
          {record.contractId && (
            <>
              {record.isContractPaid ? (
                <div className="text-primary font-normal">
                  {t('Charged to')}: {getClientDateFormat(record.chargedTo)}
                  <div className="text-success">{t('Contract is paid')}</div>
                </div>
              ) : (
                <div className="text-primary font-normal">
                  {t('Charged to')}: {getClientDateFormat(record.chargedTo)}
                  <div className="text-error">{t('Contract is unpaid')}</div>
                </div>
              )}
            </>
          )}
          {record.expirationDate && record.reservationId && (
            <div className="text-primary font-normal">
              {t('Expiry date', { ns: 'common' })}:{' '}
              <span className="text-warn font-normal">{getClientDateFormat(record.expirationDate)}</span>
            </div>
          )}
        </>
      ),
    },
    {
      title: t('Availability'),
      key: 'availability',
      render: (_, record) => (
        <ActivationBoxModal
          box={record}
          action={
            <div>
              <Toggle checked={record.isActive} isDisabled={!isManagement} />
              <div className="text-xs text-primaryLight mt-2">{t(record.isActive ? 'Enabled' : 'Disabled')}</div>
            </div>
          }
        />
      ),
    },
    {
      title: t('Note'),
      key: 'note',
      render: (_, record) => (
        <Paragraph rows={2} className="text-primaryLight font-normal">
          {record.note}
        </Paragraph>
      ),
    },
  ];

  const resultColumns = !isDesktop ? mobileColumns : [...mobileColumns, ...desktopColumns];

  return resultColumns;
};
